import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';

import LoginPage                from './pages/auth/LoginPage';
import TeamDashboard            from './pages/team/TeamDashboard';
import CoachDashboard           from './pages/coach/CoachDashboard';
import CoachCompanyPage         from './pages/coach/CoachCompanyPage';
import CoachSessionsPage        from './pages/coach/CoachSessionsPage';
import CoachAssessmentsPage     from './pages/coach/CoachAssessmentsPage';
import BeneficiaryDashboard     from './pages/beneficiary/BeneficiaryDashboard';
import AssessmentPage           from './pages/beneficiary/AssessmentPage';
import BeneficiarySessionsPage  from './pages/beneficiary/BeneficiarySessionsPage';
import CanvasPage               from './pages/shared/CanvasPage';
import JourneyMapPage          from './pages/shared/JourneyMapPage';
import ClientDashboard          from './pages/client/ClientDashboard';

import './styles/global.css';

class ErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { hasError: false, error: null }; }
  static getDerivedStateFromError(error) { return { hasError: true, error }; }
  render() {
    if (this.state.hasError) {
      return (
        <div className="gm-center-page">
          <h2>Something went wrong</h2>
          <p style={{ color: '#6b6b6b', maxWidth: 400, textAlign: 'center' }}>
            {this.state.error?.message || 'An unexpected error occurred.'}
          </p>
          <button className="gm-btn gm-btn-primary"
            onClick={() => { this.setState({ hasError: false }); window.location.reload(); }}>
            Reload page
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

function Unauthorized() {
  return (
    <div className="gm-center-page">
      <h1>Access restricted</h1>
      <p>You don't have permission to view this page.</p>
      <a href="/scaling/login" className="gm-btn gm-btn-primary">Back to sign in</a>
    </div>
  );
}

function ComingSoon({ title }) {
  return (
    <div className="gm-center-page">
      <h2>{title}</h2>
      <p style={{ color: '#6b6b6b' }}>Coming soon — check back shortly.</p>
    </div>
  );
}

const wrap = (roles, Component) => (
  <ProtectedRoute allowedRoles={roles}>
    <ErrorBoundary><Component /></ErrorBoundary>
  </ProtectedRoute>
);

export default function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            <Route path="/"        element={<Navigate to="/scaling" replace />} />
            <Route path="/scaling" element={<Navigate to="/scaling/login" replace />} />
            <Route path="/scaling/login"        element={<LoginPage />} />
            <Route path="/scaling/unauthorized" element={<Unauthorized />} />

            {/* Team */}
            <Route path="/scaling/team"    element={wrap(['team'], TeamDashboard)} />
            <Route path="/scaling/team/*"  element={wrap(['team'], TeamDashboard)} />

            {/* Coach */}
            <Route path="/scaling/coach"                          element={wrap(['coach'], CoachDashboard)} />
            <Route path="/scaling/coach/sessions"                 element={wrap(['coach'], CoachSessionsPage)} />
            <Route path="/scaling/coach/assessments"              element={wrap(['coach'], CoachAssessmentsPage)} />
            <Route path="/scaling/coach/company/:companyId"       element={wrap(['coach'], CoachCompanyPage)} />
            <Route path="/scaling/coach/*"                        element={wrap(['coach'], CoachDashboard)} />

            {/* Beneficiary */}
            <Route path="/scaling/company"             element={wrap(['beneficiary'], BeneficiaryDashboard)} />
            <Route path="/scaling/company/assessment"  element={wrap(['beneficiary'], AssessmentPage)} />
            <Route path="/scaling/company/sessions"    element={wrap(['beneficiary'], BeneficiarySessionsPage)} />
            <Route path="/scaling/company/canvas"      element={wrap(['beneficiary'], CanvasPage)} />
            <Route path="/scaling/company/journey"     element={wrap(['beneficiary'], JourneyMapPage)} />
            <Route path="/scaling/company/documents"   element={<ProtectedRoute allowedRoles={['beneficiary']}><ComingSoon title="Documents" /></ProtectedRoute>} />
            <Route path="/scaling/company/messages"    element={<ProtectedRoute allowedRoles={['beneficiary']}><ComingSoon title="Messages" /></ProtectedRoute>} />

            {/* Client */}
            <Route path="/scaling/client"   element={wrap(['client'], ClientDashboard)} />
            <Route path="/scaling/client/*" element={wrap(['client'], ClientDashboard)} />

            {/* Future modules */}
            <Route path="/project-management/*" element={<ComingSoon title="Project Management" />} />
            <Route path="/msme-fund/*"           element={<ComingSoon title="MSME Fund" />} />

            <Route path="*" element={<Navigate to="/scaling/login" replace />} />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}
